import {useState, useRef, useEffect, useCallback, KeyboardEvent, forwardRef, useImperativeHandle} from 'react'
import {Link2, Tag, Type} from 'lucide-react'
import {Textarea} from '@/components/ui/textarea'
import {cn} from '@/lib/utils'

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
const Key = {
    ARROW_DOWN: 'ArrowDown',
    ARROW_UP: 'ArrowUp',
    TAB: 'Tab',
    ENTER: 'Enter',
    ESCAPE: 'Escape',
} as const
/* eslint-enable i18next/no-literal-string */

export interface Suggestion {
    /** Text shown in the dropdown. */
    value: string
    /**
     * Legacy splice hint — text to replace the trailing identifier under the
     * cursor when no explicit replace range is provided. Defaults to {@link value}.
     */
    insertText?: string
    description: string
    kind?: 'STATIC_FIELD' | 'ATTRIBUTE' | 'RELATIONSHIP_ALIAS' | null
    /**
     * Engine-supplied explicit edit: replace `source.slice(replaceStart, replaceEnd)`
     * with `replaceText`. When defined the editor does NOT compute its own
     * splice from the cursor's trailing identifier — the backend has already
     * told it exactly which range to overwrite. Cursor lands at
     * `replaceStart + replaceText.length` after acceptance.
     */
    replaceStart?: number
    replaceEnd?: number
    replaceText?: string
}

export interface CompletionEditorProps {
    value: string
    onChange: (value: string) => void
    suggestions: Suggestion[]
    onSuggestionsRequested: (text: string, cursorPosition: number) => void
    placeholder?: string
    className?: string
    rows?: number
    readOnly?: boolean
    textareaTestId?: string
    onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void
    helpHint?: React.ReactNode
    style?: React.CSSProperties
    'data-testid'?: string
}

export interface CompletionEditorRef {
    focus: () => void
    getTextarea: () => HTMLTextAreaElement | null
}

export function getTokenBeforeCursor(text: string, pos: number): string {
    // WHY: only match the trailing identifier segment (no dot) — the suggestion
    // contract is tail-only after CEL migration. `identity.|` returns "" so
    // the splice inserts `type` AFTER the dot instead of replacing `identity.`
    // entirely. `identity.t|` returns "t" so the splice replaces `t` with `type`.
    const beforeCursor = text.substring(0, pos)
    const tokenMatch = beforeCursor.match(/[\w]+$/)
    return tokenMatch ? tokenMatch[0] : ''
}

export const CompletionEditor = forwardRef<CompletionEditorRef, CompletionEditorProps>(function CompletionEditor({
    value,
    onChange,
    suggestions,
    onSuggestionsRequested,
    placeholder,
    className,
    rows = 6,
    readOnly,
    textareaTestId,
    onKeyDown: externalKeyDown,
    helpHint,
    style,
    'data-testid': dataTestId,
}, ref) {
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [isEditing, setIsEditing] = useState(false)
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const suggestionRefs = useRef<Array<HTMLButtonElement | null>>([])

    // WHY: keyboard ArrowDown/ArrowUp must scroll the highlighted
    // suggestion into view; otherwise the user can move the selection past the
    // dropdown's max-h-48 visible window and the field of view stays put.
    useEffect(() => {
        const el = suggestionRefs.current[selectedIndex]
        if (el) {
            el.scrollIntoView({block: 'nearest'})
        }
    }, [selectedIndex])

    // Undo/redo history
    const historyRef = useRef<{ states: string[]; index: number }>({states: [value], index: 0})
    const isUndoRedoRef = useRef(false)

    useImperativeHandle(ref, () => ({
        focus: () => textareaRef.current?.focus(),
        getTextarea: () => textareaRef.current,
    }))

    // Show/hide suggestions based on what the parent provides
    const showOrHide = suggestions.length > 0 && showSuggestions && isEditing

    // Push to history (for undo/redo)
    const pushHistory = useCallback((newValue: string) => {
        if (isUndoRedoRef.current) {
            isUndoRedoRef.current = false
            return
        }
        const history = historyRef.current
        history.states = history.states.slice(0, history.index + 1)
        if (history.states[history.index] !== newValue) {
            history.states.push(newValue)
            history.index = history.states.length - 1
            if (history.states.length > 100) {
                history.states.shift()
                history.index--
            }
        }
    }, [])

    const undo = useCallback(() => {
        const history = historyRef.current
        if (history.index > 0) {
            history.index--
            isUndoRedoRef.current = true
            const newValue = history.states[history.index]
            onChange(newValue)
            return true
        }
        return false
    }, [onChange])

    const redo = useCallback(() => {
        const history = historyRef.current
        if (history.index < history.states.length - 1) {
            history.index++
            isUndoRedoRef.current = true
            const newValue = history.states[history.index]
            onChange(newValue)
            return true
        }
        return false
    }, [onChange])

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value
        const newCursorPos = e.target.selectionStart || 0
        onChange(newValue)
        pushHistory(newValue)
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
            blurTimeoutRef.current = null
        }
        setIsEditing(true)
        setShowSuggestions(true)
        onSuggestionsRequested(newValue, newCursorPos)
    }

    const handleFocus = () => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
            blurTimeoutRef.current = null
        }
        setIsEditing(true)
        const pos = textareaRef.current?.selectionStart || 0
        setShowSuggestions(true)
        onSuggestionsRequested(value, pos)
    }

    const handleSelect = () => {
        if (textareaRef.current) {
            const pos = textareaRef.current.selectionStart || 0
            // WHY: ask the engine for fresh suggestions whenever the cursor
            // moves — the replace range depends on cursor position, so a
            // suggestion list computed at the previous offset would splice
            // at the wrong place. Run unconditionally; the parent's debounce
            // collapses duplicate calls.
            onSuggestionsRequested(value, pos)
        }
    }

    const applySuggestion = useCallback((suggestion: Suggestion) => {
        // WHY: the backend dictates the exact edit — replace
        // `source.slice(replaceStart, replaceEnd)` with `replaceText`. No
        // re-parsing on the frontend, no guessing about token boundaries.
        // The engine has the AST + cursor; it knows the right range.
        if (typeof suggestion.replaceStart !== 'number'
                || typeof suggestion.replaceEnd !== 'number'
                || typeof suggestion.replaceText !== 'string') {
            throw new Error(`Suggestion is missing replace range: ${suggestion.value}`)
        }
        const beforeToken = value.substring(0, suggestion.replaceStart)
        const afterCursor = value.substring(suggestion.replaceEnd)
        const splice = suggestion.replaceText

        const newValue = beforeToken + splice + afterCursor
        onChange(newValue)
        pushHistory(newValue)

        const newPosition = beforeToken.length + splice.length
        // WHY: chained completion — after Tab/Enter the cursor lands at a new
        // slot (e.g. `==` accepted moves the cursor to value position). The
        // backend would happily emit enum-value completions for that slot, but
        // only if asked. Trigger a fresh fetch so the user doesn't have to
        // click or type to re-open the dropdown.
        onSuggestionsRequested(newValue, newPosition)
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.selectionStart = newPosition
                textareaRef.current.selectionEnd = newPosition
                textareaRef.current.focus()
            }
        }, 0)
    }, [value, onChange, pushHistory, onSuggestionsRequested])

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
            if (e.shiftKey) {
                if (redo()) {
                    e.preventDefault()
                }
            } else {
                if (undo()) {
                    e.preventDefault()
                }
            }
            return
        }

        if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
            if (redo()) {
                e.preventDefault()
            }
            return
        }

        if (showOrHide) {
            switch (e.key) {
                case Key.ARROW_DOWN:
                    e.preventDefault()
                    setSelectedIndex(i => (i + 1) % suggestions.length)
                    return
                case Key.ARROW_UP:
                    e.preventDefault()
                    setSelectedIndex(i => (i - 1 + suggestions.length) % suggestions.length)
                    return
                case Key.TAB:
                case Key.ENTER:
                    if (suggestions[selectedIndex]) {
                        e.preventDefault()
                        applySuggestion(suggestions[selectedIndex])
                    }
                    return
                case Key.ESCAPE:
                    e.preventDefault()
                    setShowSuggestions(false)
                    return
            }
        }

        externalKeyDown?.(e)
    }

    const handleBlur = () => {
        blurTimeoutRef.current = setTimeout(() => {
            setShowSuggestions(false)
            setIsEditing(false)
            blurTimeoutRef.current = null
        }, 200)
    }

    return (
        <div className="relative" data-testid={dataTestId}>
            <Textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onSelect={handleSelect}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onFocus={handleFocus}
                placeholder={placeholder}
                className={cn('font-mono text-sm', className)}
                rows={rows}
                readOnly={readOnly}
                style={style}
                data-testid={textareaTestId}
            />

            {/* Suggestions dropdown */}
            {showOrHide && (
                <div
                    className="absolute z-50 mt-1 w-full max-h-48 overflow-auto rounded-md border bg-popover shadow-lg">
                    {suggestions.map((suggestion, index) => (
                        <button
                            key={`${index}-${suggestion.value}`}
                            ref={el => { suggestionRefs.current[index] = el }}
                            type="button"
                            className={cn(
                                'w-full px-3 py-2 text-left text-sm flex justify-between items-center',
                                index === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                            )}
                            onMouseDown={(e) => {
                                e.preventDefault()
                                applySuggestion(suggestion)
                            }}
                            onMouseEnter={() => setSelectedIndex(index)}
                            data-testid={`btn-suggestion-${index}`}
                            data-selected={index === selectedIndex ? 'true' : undefined}
                        >
                            <span className="flex items-center gap-2 min-w-0">
                                {suggestion.kind === 'STATIC_FIELD' && (
                                    <Type className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                                          data-testid={`icon-static-field-${index}`}/>
                                )}
                                {suggestion.kind === 'ATTRIBUTE' && (
                                    <Tag className="h-3.5 w-3.5 text-primary shrink-0"
                                         data-testid={`icon-attribute-${index}`}/>
                                )}
                                {suggestion.kind === 'RELATIONSHIP_ALIAS' && (
                                    <Link2 className="h-3.5 w-3.5 text-primary shrink-0"
                                           data-testid={`icon-relationship-alias-${index}`}/>
                                )}
                                <code className="font-mono truncate">{suggestion.value}</code>
                            </span>
                            <span className="text-muted-foreground text-xs ml-2">{suggestion.description}</span>
                        </button>
                    ))}
                </div>
            )}

            {helpHint}
        </div>
    )
})
